import { z } from 'zod'
import './registry'
import { IsoDateSchema } from './common'

/**
 * Per-tenant editable content pages (webshop plan §4): FAQ, terms, privacy,
 * about, shipping-info, returns/withdrawal — the markdown the storefront's
 * `/info`-style pages render. Persisted as `T#<tenantId>` /
 * `CONTENT#<slug>#<locale>` items: one item per locale, so an upsert of the
 * Dutch text can never clobber the English one and the public fallback
 * (`localeUsed`, §0.5 of the api plan) is a second targeted GetItem, not a
 * filter.
 *
 * Who writes the content is open decision D7 — it only affects how these
 * items get seeded, never their shape.
 */

export const ContentLocaleSchema = z.enum(['nl', 'en']).openapi('ContentLocale')

/** URL slug of a content page, e.g. "verzending", "voorwaarden", "privacy". */
export const ContentSlugSchema = z
  .string()
  .regex(/^[a-z0-9-]+$/, 'lowercase url slug')
  .max(60)
  .openapi('ContentSlug', { example: 'verzending' })

/**
 * Wire-shape of a stored content page. Parses raw DDB items (default strip
 * mode drops PK/SK) — do not `.strict()` it.
 */
export const ContentPageSchema = z
  .object({
    slug: ContentSlugSchema,
    locale: ContentLocaleSchema,
    title: z.string().min(1).max(200),
    bodyMarkdown: z.string().max(20000),
    /** Unpublished pages are visible to staff only — never on the storefront. */
    published: z.boolean().default(false),
    updatedAt: IsoDateSchema,
  })
  .openapi('ContentPage')

/**
 * PUT /webshop/pages/{slug} body — one locale per call, matching the storage
 * key (upserting NL can never touch EN). Slug comes from the path.
 */
export const PutContentPageRequestSchema = z
  .object({
    locale: ContentLocaleSchema,
    title: z.string().min(1).max(200),
    bodyMarkdown: z.string().max(20000),
    published: z.boolean(),
  })
  .strict()
  .openapi('PutContentPageRequest')

/** Staff list row (GET /webshop/pages) — everything but the body. */
export const ContentPageSummarySchema = ContentPageSchema.omit({ bodyMarkdown: true }).openapi(
  'ContentPageSummary'
)

/**
 * Public page response (GET /shop/pages/{slug}). `localeUsed` names the locale
 * actually served: it differs from `locale`'s request intent when the page has
 * no published variant in the requested locale and the NL fallback was served
 * — the UI shows "alleen in het Nederlands beschikbaar" off this field.
 */
export const ShopContentPageSchema = z
  .object({
    slug: ContentSlugSchema,
    localeRequested: ContentLocaleSchema,
    localeUsed: ContentLocaleSchema,
    title: z.string(),
    bodyMarkdown: z.string(),
    updatedAt: IsoDateSchema,
  })
  .openapi('ShopContentPage')

/** Public nav/footer list row (GET /shop/pages). */
export const ShopContentPageLinkSchema = z
  .object({
    slug: ContentSlugSchema,
    title: z.string(),
  })
  .openapi('ShopContentPageLink')

export type ContentLocale = z.infer<typeof ContentLocaleSchema>
export type ContentPage = z.infer<typeof ContentPageSchema>
export type ContentPageSummary = z.infer<typeof ContentPageSummarySchema>
export type PutContentPageRequest = z.infer<typeof PutContentPageRequestSchema>
export type ShopContentPage = z.infer<typeof ShopContentPageSchema>
export type ShopContentPageLink = z.infer<typeof ShopContentPageLinkSchema>

// Content-page item keys — shared by lib/ddb.ts and
// scripts/seed-content-pages.ts (the RECHIST lesson: a script and a handler
// sharing a key shape share its definition). They live in the import-free
// ./webshop-keys so lib/ddb.ts can name them without loading this schema.
export { contentPageKeys } from './webshop-keys'
